import {
  Avatar,
  CardHeader,
  Grid,
  IconButton,
  Link,
  Tooltip,
  useTheme,
} from "@mui/material";
import GridListItem from "components/common/grid/GridListItem";
import CopyToClipboard from "components/common/copy/CopyToClipboard";
import React from "react";

import { KeyboardDoubleArrowLeftRounded } from "@mui/icons-material";
import { getFullName, getInitials } from "utils/string";
import BillingInfo from "./BillingInfo";

import {
  toggleDrawer,
  changePatient,
  toggleModal,
} from "app/store/transmission/slice/transmissionSlice";
import { useDispatch } from "react-redux";
import {
  checkIsRpmManufacturer,
  filterRpmManufacturers,
} from "utils/rpmDevice.util";
import MedicationInfo from "./MedicationInfo";

const PatientInfo = ({ data }) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const manufacturers = [
    ...new Set([data?.manufacturer, ...(data?.additional_manufacturer || [])]),
  ];

  const isRPMReport = checkIsRpmManufacturer(data?.manufacturer);
  const rpmManufacturers = filterRpmManufacturers(manufacturers);

  const device = isRPMReport
    ? rpmManufacturers?.length > 1
      ? rpmManufacturers?.join(", ")
      : data?.device || data?.manufacturer
    : data?.patient?.deviceInformation?.model_display_name ||
      data?.device ||
      data?.patient?.deviceInformation?.modelName ||
      "";

  const getDeviceDoi = () => {
    const deviceData = data?.patient?.previous_devices?.find(
      ({ device_model, device_serial }) =>
        device_model === data?.device &&
        device_serial === data?.device_serial_number
    );

    return deviceData ? deviceData?.doi : data?.patient?.doi;
  };

  const handlePatientNameClick = () => {
    dispatch(
      changePatient({
        patientId: data?.patient?._id,
        serialNumber: data?.patient_serial_num,
        account: data?.account,
      })
    );
    dispatch(toggleModal({ type: "patient", value: true }));
  };

  return (
    <>
      <CardHeader
        style={{ alignItems: "flex-start" }}
        avatar={
          <Avatar className="view-pdf__drawer--avatar" alt={data?.name}>
            {getInitials(data?.name)}
          </Avatar>
        }
        action={
          <Tooltip title="Close Patient Info">
            <IconButton
              aria-label="close drawer"
              onClick={() => dispatch(toggleDrawer(false))}
            >
              <KeyboardDoubleArrowLeftRounded
                style={{
                  color:
                    theme.palette.type === "dark"
                      ? theme.palette.primary.main
                      : "#949494",
                }}
              />
            </IconButton>
          </Tooltip>
        }
        title={
          <Tooltip title="View Patient Profile">
            <Link
              component="button"
              underline="hover"
              onClick={handlePatientNameClick}
              className="d-flex align-items-center"
              sx={{ fontSize: "18px", fontWeight: 500 }}
            >
              {getFullName(data?.name)}
            </Link>
          </Tooltip>
        }
        titleTypographyProps={{
          style: { fontSize: "18px", fontWeight: 500 },
        }}
        subheader={
          <div
            className="d-flex flex-column"
            style={{ wordBreak: "break-word" }}
          >
            <div className="d-flex align-items-center" style={{ gap: "4px" }}>
              {`ID: #${data?.patient?.evoke_id ?? "-"}`}
              {data?.patient?.evoke_id && (
                <CopyToClipboard value={data?.patient?.evoke_id} />
              )}
            </div>
            <div className="d-flex align-items-center" style={{ gap: "4px" }}>
              {`MRN: ${data?.patient?.mrn ?? "-"}`}
              {data?.patient?.mrn && (
                <CopyToClipboard value={data?.patient?.mrn} />
              )}
            </div>
          </div>
        }
        subheaderTypographyProps={{
          style: {
            fontSize: "14px",
            fontWeight: 500,
          },
        }}
      />

      <div style={{ padding: "8px 16px" }}>
        <div
          className="view-pdf__drawer--profile-report d-flex align-items-center"
          style={{ gap: "4px" }}
        >
          {`Report ID: ${data?.reportId}`}
          {data?.reportId && <CopyToClipboard value={data?.reportId} />}
        </div>
        <div className="view-pdf__drawer--header">Basic Info</div>
        <div className="view-pdf__drawer--profile">
          <Grid container columnSpacing={2} className="view-pdf__drawer-grid">
            <Grid item container rowSpacing={2}>
              <GridListItem
                title="DOB"
                subTitle={data?.patient?.dob}
                copyValue={data?.patient?.dob}
                isDate
              />
              <GridListItem title="DOI" subTitle={getDeviceDoi()} isDate />
            </Grid>

            <GridListItem size={12} title="DEVICE" subTitle={device || "-"} />

            <GridListItem
              size={12}
              title="DEVICE SERIAL NUMBER"
              subTitle={data?.device_serial_number || "-"}
            />

            <GridListItem
              title="FIN"
              subTitle={data?.clinic_notes?.split("\n\nFIN: ")?.[1] || "-"}
            />
            <GridListItem
              title="HX STROKE"
              subTitle={data?.patient?.information?.strokeHistory || "-"}
            />
            <GridListItem
              size={12}
              title="HOSPITAL"
              subTitle={data?.patient?.hospital || "-"}
            />
            <GridListItem
              size={12}
              title="ASSIGNED PHYSICIAN"
              subTitle={data?.assignedUser?.name || "-"}
            />
            <GridListItem
              size={12}
              title="PRIMARY INSURANCE"
              subTitle={
                (data?.patient?.insurance?.active &&
                  data?.patient?.insurance?.name) ||
                (data?.patient?.secondary_insurance?.active &&
                  data?.patient?.secondary_insurance?.name) ||
                (data?.patient?.tertiary_insurance?.active &&
                  data?.patient?.tertiary_insurance?.name) ||
                "-"
              }
            />
          </Grid>
        </div>

        <div className="view-pdf__drawer-info-section">
          <BillingInfo reportId={data?._id} />
        </div>

        <div className="view-pdf__drawer-info-section">
          <MedicationInfo patientId={data?.patient?._id} />
        </div>
      </div>
    </>
  );
};

export default PatientInfo;
